/**
 * /start deep links into the public order flow (StartFromMarketingPage).
 *
 * Everything here is pure (or takes its fetch/storage as an argument) so the
 * rules can be unit-tested without a browser:
 *
 *   parseStartParams    query string -> validated inputs (junk dropped)
 *   resolvePlanRequest  inputs + LIVE /api/public/plans -> the Plan, by tier
 *                       NAME and interval, never by id meaning
 *   decideStartRoute    everything -> where to send the visitor
 *   checkNumberAvailable  the number picker's own public search, read-only
 *
 * Nothing in a link is trusted for money: the price, name and interval always
 * come from the live plan list, and the server bills the plan row's own Stripe
 * price. Checkout keeps every anti-fraud step (email check, mobile OTP, Google
 * email_verified, VPN and duplicate-order gates) — a deep link only skips the
 * plan PICKER, never checkout itself.
 */
import { parsePhoneNumberFromString, getCountryCallingCode, type CountryCode } from "libphonenumber-js";
import type { Plan, PlanBase } from "./orderFlow";
import { baseForPlanId, intervalForPlan, planAt, resolveOfferedPlan, type BillingInterval } from "./orderPlanCatalog";

export type PlanTier = "lite" | "starter" | "growth";
const TIERS: ReadonlySet<string> = new Set(["lite", "starter", "growth"]);

/** The tiers this page sells only on the yearly price: no billing= means annual. */
const ANNUAL_DEFAULT_TIERS: ReadonlySet<string> = new Set(["lite"]);

/** The number-picker vocabulary (landing-number-search stores these). */
export type StartNumberType = "local" | "mobile" | "tollFree";
const NUMBER_TYPE_ALIASES: Record<string, StartNumberType> = {
  local: "local",
  mobile: "mobile",
  tollfree: "tollFree",
  "toll-free": "tollFree",
  toll_free: "tollFree",
};

/** Strict E.164: a +, a non-zero digit, 7-15 digits in all. */
export const E164 = /^\+[1-9]\d{6,14}$/;
const ISO2 = /^[A-Z]{2}$/;
/** NANP toll-free: shared by the US and Canada (libphonenumber labels it US). */
const NANP_TOLL_FREE = /^\+18(00|33|44|55|66|77|88)\d{7}$/;
const US_CA: ReadonlySet<string> = new Set(["US", "CA"]);
/** A raw catalogue id (back-compat links: ?plan=starter_yearly). */
const PLAN_ID = /^[a-z0-9][a-z0-9_-]{1,60}$/i;

export type PlanRequest =
  | { kind: "tier"; tier: PlanTier; interval: BillingInterval }
  | { kind: "id"; id: string };

export interface StartParams {
  /** Valid E.164 whose calling code matches the country, else null. */
  number: string | null;
  /** A number was in the link but was malformed or didn't match the country. */
  numberRejected: boolean;
  /** ISO-2. From the link when valid, else derived from the number. Null = unknown. */
  country: string | null;
  numberType: StartNumberType;
  plan: PlanRequest | null;
}

function parseBilling(raw: string | null): BillingInterval | null {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "annual" || v === "yearly" || v === "year") return "year";
  if (v === "monthly" || v === "month") return "month";
  return null;
}

/** ?plan= and ?billing= -> what the visitor asked for, or null for junk. */
export function parsePlanRequest(rawPlan: string | null, rawBilling: string | null): PlanRequest | null {
  const v = String(rawPlan ?? "").trim();
  if (!v) return null;
  const lower = v.toLowerCase();
  if (TIERS.has(lower)) {
    const tier = lower as PlanTier;
    const interval = parseBilling(rawBilling) ?? (ANNUAL_DEFAULT_TIERS.has(tier) ? "year" : "month");
    return { kind: "tier", tier, interval };
  }
  if (PLAN_ID.test(v) && lower !== "trial") return { kind: "id", id: v };
  return null;
}

/** Validate every input; anything unknown is dropped, never passed through. */
export function parseStartParams(params: URLSearchParams): StartParams {
  const rawNumber = (params.get("number") || "").trim();
  const rawCountry = (params.get("country") || "").trim().toUpperCase();
  const linkCountry = ISO2.test(rawCountry) ? rawCountry : null;
  const numberType = NUMBER_TYPE_ALIASES[(params.get("numberType") || "").trim().toLowerCase()] ?? "local";
  const plan = parsePlanRequest(params.get("plan"), params.get("billing"));

  let number: string | null = null;
  let country: string | null = linkCountry;
  let numberRejected = false;
  if (rawNumber) {
    const parsed = E164.test(rawNumber) ? parsePhoneNumberFromString(rawNumber) : undefined;
    if (!parsed || !parsed.isPossible()) {
      numberRejected = true;
    } else if (linkCountry) {
      // The country decides whether the KYC step runs, so a link must not be
      // able to pair a +44 number with country=US and skip it.
      // When libphonenumber places the number in a region, that region must
      // be the link's: +1 876 is Jamaica and +44 1534 is Jersey, not US/GB.
      // The server refuses the same mismatch (server/lib/orderNumberCountry).
      let code: string | null = null;
      try { code = getCountryCallingCode(linkCountry as CountryCode); } catch { code = null; }
      const region = parsed.country ?? null;
      if (!code || code !== parsed.countryCallingCode) {
        numberRejected = true;
      } else if (!region || region === linkCountry || (NANP_TOLL_FREE.test(rawNumber) && US_CA.has(linkCountry))) {
        // Unplaceable but same calling code, or NANP toll-free (shared by US/CA).
        number = rawNumber;
      } else if (US_CA.has(region) && US_CA.has(linkCountry)) {
        // A Canadian number linked as US (or the reverse): same flow, real country.
        number = rawNumber;
        country = region;
      } else {
        numberRejected = true;
      }
    } else if (parsed.country) {
      number = rawNumber;
      country = parsed.country;
    } else {
      // A possible number whose country we can't tell: let them pick again.
      numberRejected = true;
    }
  }
  return { number, numberRejected, country, numberType, plan };
}

export type PlanNotice = "lite_annual_only";

export interface ResolvedPlan {
  plan: Plan;
  /** Exactly what was asked for and currently sold: eligible to skip to checkout. */
  exact: boolean;
  /** Set when the plan was swapped for the one this page sells instead. */
  notice?: PlanNotice;
}

/**
 * The live Plan a request names. Tiers map by plan NAME and interval — never
 * through the yearly ids, which are named one tier off (starter_yearly is
 * Lite). A plan asked for on an interval it is not sold on (Lite monthly,
 * friendly or a legacy lite-plan-monthly id) resolves to the interval it IS
 * sold on, marked not-exact with a notice, so the visitor sees the new price on
 * the plan step before anything else.
 */
export function resolvePlanRequest(basePlans: PlanBase[], req: PlanRequest | null): ResolvedPlan | null {
  if (!req) return null;
  if (req.kind === "tier") {
    const base = basePlans.find((b) => b.name.trim().toLowerCase() === req.tier);
    if (!base) return null;
    const interval = intervalForPlan(base, req.interval);
    const plan = interval ? planAt(base, interval) : undefined;
    if (!plan) return null;
    return interval === req.interval ? { plan, exact: true } : { plan, exact: false, notice: "lite_annual_only" };
  }
  const offered = resolveOfferedPlan(basePlans, req.id);
  if (offered) return { plan: offered, exact: true };
  const base = baseForPlanId(basePlans, req.id);
  if (!base) return null;
  const wanted: BillingInterval = base.yearlyPlanId === req.id ? "year" : "month";
  const interval = intervalForPlan(base, wanted);
  const plan = interval ? planAt(base, interval) : undefined;
  return plan ? { plan, exact: false, notice: "lite_annual_only" } : null;
}

export type Availability = "available" | "taken" | "unknown";
export type RouteNotice = "number_taken" | "number_unconfirmed" | "number_invalid" | PlanNotice;

export interface StartRoute {
  path: string;
  /** Keep the number (it will be bought); false = forget it and pick again. */
  keepNumber: boolean;
  /** Store this plan (all four keys); null = leave/clear the plan. */
  plan: Plan | null;
  /** After the KYC step, go straight to checkout instead of the plan picker. */
  skipPlanAfterKyc: boolean;
  notice?: RouteNotice;
}

function searchPath(country: string | null): string {
  return country && ISO2.test(country) ? `/?country=${country}` : "/";
}

/** Whether the availability check is needed before routing (only when skipping the plan step). */
export function wantsCheckout(p: StartParams, resolved: ResolvedPlan | null): boolean {
  return !!p.number && !!p.country && !!resolved?.exact;
}

/**
 * Where /start sends the visitor. `availability` matters only when
 * wantsCheckout() — pass undefined otherwise.
 */
export function decideStartRoute(input: {
  params: StartParams;
  resolved: ResolvedPlan | null;
  kycRequired: boolean;
  availability?: Availability;
}): StartRoute {
  const { params: p, resolved, kycRequired, availability } = input;
  const plan = resolved?.plan ?? null;

  if (!p.number || !p.country) {
    return {
      path: searchPath(p.country),
      keepNumber: false,
      plan,
      skipPlanAfterKyc: false,
      notice: p.numberRejected ? "number_invalid" : resolved?.notice,
    };
  }

  if (wantsCheckout(p, resolved)) {
    if (availability !== "available") {
      return {
        path: searchPath(p.country),
        keepNumber: false,
        plan,
        skipPlanAfterKyc: false,
        notice: availability === "taken" ? "number_taken" : "number_unconfirmed",
      };
    }
    return kycRequired
      ? { path: "/kyc-address", keepNumber: true, plan, skipPlanAfterKyc: true }
      : { path: "/checkout", keepNumber: true, plan, skipPlanAfterKyc: false };
  }

  // A number but no plan, an unknown plan, or one that changed (Lite monthly):
  // the plan step, after KYC where the country needs it.
  return {
    path: kycRequired ? "/kyc-address" : "/select-plan",
    keepNumber: true,
    plan,
    skipPlanAfterKyc: false,
    notice: resolved?.notice,
  };
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Pick<Response, "ok" | "status" | "json">>;

/**
 * Is this number still for sale? Uses the number picker's own public search
 * (POST /api/public/search-numbers, per-IP rate-limited, read-only) on the
 * number's national digits. Only an exact match is "available": the endpoint
 * falls back to unfiltered results when nothing matches, so a list of OTHER
 * numbers is a "taken". A 404 is "taken"; any other failure is "unknown".
 */
export async function checkNumberAvailable(
  number: string,
  country: string,
  numberType: StartNumberType,
  fetchImpl: FetchLike = fetch,
): Promise<Availability> {
  const digits = parsePhoneNumberFromString(number)?.nationalNumber;
  if (!digits) return "unknown";
  try {
    const res = await fetchImpl("/api/public/search-numbers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ country, numberType, contains: String(digits) }),
    });
    if (res.status === 404) return "taken";
    if (!res.ok) return "unknown";
    const data = (await res.json()) as { numbers?: Array<{ phoneNumber?: string }> };
    const list = Array.isArray(data?.numbers) ? data.numbers : [];
    return list.some((n) => n?.phoneNumber === number) ? "available" : "taken";
  } catch {
    return "unknown";
  }
}

/**
 * The LIVE plan list, or null when it can't be had. Deliberately not
 * fetchPlansFromAPI, which falls back to a built-in catalogue: a deep link
 * only skips the plan step on prices the server just returned.
 */
export async function fetchLivePlans(fetchImpl: FetchLike = fetch): Promise<PlanBase[] | null> {
  try {
    const res = await fetchImpl("/api/public/plans");
    if (!res.ok) return null;
    const data = (await res.json()) as { success?: boolean; plans?: PlanBase[] };
    return data?.success && Array.isArray(data.plans) && data.plans.length > 0 ? data.plans : null;
  } catch {
    return null;
  }
}

// ─── One-shot notices and the post-KYC skip ─────────────────────────────────

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const ORDER_NOTICE_KEY = "orderFlowNotice";
export const SKIP_PLAN_AFTER_KYC_KEY = "orderSkipPlanAfterKyc";
const NOTICES: ReadonlySet<string> = new Set(["number_taken", "number_unconfirmed", "number_invalid", "lite_annual_only"]);

export function setOrderNotice(storage: Store, notice: RouteNotice | undefined): void {
  try {
    if (notice) storage.setItem(ORDER_NOTICE_KEY, notice);
    else storage.removeItem(ORDER_NOTICE_KEY);
  } catch { /* storage unavailable: no notice */ }
}

/** The pending notice, if any (only known values). Clear it with clearOrderNotice once shown. */
export function peekOrderNotice(storage: Store): RouteNotice | null {
  try {
    const v = storage.getItem(ORDER_NOTICE_KEY);
    return v && NOTICES.has(v) ? (v as RouteNotice) : null;
  } catch {
    return null;
  }
}

export function clearOrderNotice(storage: Store): void {
  try { storage.removeItem(ORDER_NOTICE_KEY); } catch { /* ignore */ }
}

/** Fixed copy for the number-search page. Lite's copy needs the live price (litePlanNotice). */
export function numberNoticeText(notice: RouteNotice | null): string | null {
  switch (notice) {
    case "number_taken": return "That number was just taken — pick another; your plan is saved.";
    case "number_unconfirmed": return "We couldn't confirm that number is still available — pick another; your plan is saved.";
    case "number_invalid": return "That link didn't include a valid number — pick one below.";
    default: return null;
  }
}

/** "Lite is now billed annually at $90/year", with the live yearly price. */
export function litePlanNotice(yearlyPriceText: string): string {
  return `Lite is now billed annually at ${yearlyPriceText}/year.`;
}

export function setSkipPlanAfterKyc(storage: Store, on: boolean): void {
  try {
    if (on) storage.setItem(SKIP_PLAN_AFTER_KYC_KEY, "1");
    else storage.removeItem(SKIP_PLAN_AFTER_KYC_KEY);
  } catch { /* ignore */ }
}

/**
 * Where the KYC step goes next. Checkout only when a /start link asked for it
 * AND a fully stored plan is waiting (checkout re-checks it against the live
 * list and sends an unsold plan back to /select-plan). One-shot.
 */
export function nextAfterKyc(storage: Store): "/checkout" | "/select-plan" {
  try {
    const skip = storage.getItem(SKIP_PLAN_AFTER_KYC_KEY) === "1";
    storage.removeItem(SKIP_PLAN_AFTER_KYC_KEY);
    const planStored = ["selectedPlanId", "selectedPlanName", "selectedPlanPrice", "selectedPlanInterval"]
      .every((k) => !!storage.getItem(k));
    return skip && planStored ? "/checkout" : "/select-plan";
  } catch {
    return "/select-plan";
  }
}
